import { Ban, CheckCircle2, CirclePause, Clock, type LucideIcon } from "lucide-react";
import { cn } from "@/modern/utils";
import type { MarketingAssetVerificationStatus } from "@/bryntum-lib/models/MarketingAsset";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/modern/components/ui/tooltip";

type StatusDisplay = {
  label: string;
  verb: string;
  icon: LucideIcon;
  chipClassName: string;
  iconClassName: string;
};

const STATUS_DISPLAY: Record<MarketingAssetVerificationStatus, StatusDisplay> = {
  accepted: {
    label: "Approved",
    verb: "Approved",
    icon: CheckCircle2,
    chipClassName: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    iconClassName: "text-emerald-600 dark:text-emerald-400",
  },
  rejected: {
    label: "Rejected",
    verb: "Rejected",
    icon: Ban,
    chipClassName: "border-destructive/30 bg-destructive/10 text-destructive",
    iconClassName: "text-destructive",
  },
  pending_review: {
    label: "In review",
    verb: "Marked for review",
    icon: Clock,
    chipClassName: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
    iconClassName: "text-amber-600 dark:text-amber-400",
  },
  skipped: {
    label: "Skipped",
    verb: "Skipped",
    icon: CirclePause,
    chipClassName: "border-border bg-muted text-muted-foreground",
    iconClassName: "text-muted-foreground",
  },
};

/** Compact header status for Edit Promotion — replaces the large banner. */
export function AssetStatusChip({
  status,
  actionedByName,
}: {
  status: MarketingAssetVerificationStatus;
  actionedByName: string | null;
}) {
  const display = STATUS_DISPLAY[status];
  if (!display) return null;
  const Icon = display.icon;
  const detail = actionedByName ? `${display.verb} by ${actionedByName}` : display.verb;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex h-7 max-w-[9rem] shrink-0 items-center gap-1 rounded-full border px-2 text-xs font-normal",
            display.chipClassName,
          )}
          role="status"
          aria-label={`Asset status: ${display.label}${actionedByName ? `, by ${actionedByName}` : ""}`}
        >
          <Icon className={cn("size-3.5 shrink-0", display.iconClassName)} aria-hidden />
          <span className="truncate">{display.label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="end"
        className="z-[calc(var(--swivu-bryntum-z-index)+20)] max-w-[220px] text-left"
      >
        {detail}
      </TooltipContent>
    </Tooltip>
  );
}

/** @deprecated Prefer AssetStatusChip in the Promote header. */
export function AssetStatusBanner({
  status,
  actionedByName,
}: {
  status: MarketingAssetVerificationStatus;
  actionedByName: string | null;
}) {
  return <AssetStatusChip status={status} actionedByName={actionedByName} />;
}
